import swal from 'sweetalert2';
import moment from 'moment-timezone';
import hateoas from 'viralstyle-ui/src/hateoas';
import storage from 'viralstyle-ui/src/storage';
import each from 'lodash/each';
import get from 'lodash/get';
import pick from 'lodash/pick';
import filter from 'lodash/filter';
import map from 'lodash/map';
import find from 'lodash/find';
import isNumber from 'lodash/isNumber';

import campaignsApi from '../_campaigns-api';
import config from 'config';
import campaignsEnum from '../_campaigns-enum';
import transformer from '../_campaigns-transformer';
import clientMixins from 'utils/client-mixins';
import { productValidator } from '../_campaigns-validator';

export const getTabData = async ({ commit, dispatch, state }, { campaignId, tab }) => {
  if (state.currentCampaign.cache.id !== campaignId) {
    commit('setCurrentCampaign', { property: 'cache', value: {} });
    commit('setCurrentCampaign', { property: 'cache.id', value: campaignId });
  }

  if (!state.currentCampaign.cache[tab]) {
    let response = await dispatch('async', () => campaignsApi.getTabData(campaignId, tab));

    switch (tab) {
      case campaignsEnum.INFO:
        commit('setCurrentCampaign', { property: tab, value: response.data, tab });
        break;
      case campaignsEnum.UPSELLS:
        commit('setCurrentCampaign', { property: 'upsellData', value: response.data, tab });
        break;
      case campaignsEnum.PRODUCTS:
        commit('setCurrentCampaign', { property: tab, value: response.data.products, tab });
        break;
      case campaignsEnum.TRACKING:
        commit('setCurrentCampaign', { property: 'pixels', value: response.data.pixels, tab });
        commit('initializeCurrentCampaignFacebookCustomVars');
        break;
      case campaignsEnum.META:
        commit('setCurrentCampaign', { property: 'seo', value: response.data.metadata, tab });
        break;
      case campaignsEnum.EMAIL:
        commit('setCurrentCampaign', { property: tab, value: response.data.list, tab });
        break;
      case campaignsEnum.ASSETS:
        commit('setCurrentCampaign', { property: tab, value: response.data.assets, tab });
        break;
      case campaignsEnum.AD_FACTORY:
        commit('setCurrentCampaign', { property: tab, value: response.data.ad_factory_url });
        break;
    }

    commit('setCurrentCampaign', { property: `cache[${tab}]`, value: true });

    return response;
  }
};

export const updateCampaignInfo = async ({ dispatch, commit, state }, { campaignId, tab }) => {
  let response = await dispatch('async', () => campaignsApi.updateCampaignInfo(campaignId, state.currentCampaign.info));

  commit('setCurrentCampaign', { property: 'cache.info', value: false });

  await dispatch('getTabData', { campaignId, tab });

  return response
};

export const getCampaignCategories = async ({ commit, state, dispatch }) => {
  if (!state.campaignCategories.length) {
    let response = await dispatch('async', () => campaignsApi.getCampaignCategories());

    commit('setCampaignCategories', response.data);

    return response;
  }
};

export const getCampaignEligibleUpsells = async ({ commit, state, dispatch }, { campaignId, lazy }) => {
  if (!state.currentCampaign.eligibleUpsells.initialized || lazy) {
    let response = await dispatch('async', () => campaignsApi.getCampaignEligibleUpsells(state.currentCampaign.eligibleUpsells, campaignId, state.currentCampaign.info.domain_id))

    commit('setEligibleUpsells', response.data);

    return response;
  }
};

export const searchCampaignEligibleUpsells = async ({ commit, dispatch }, campaignId) => {
  commit('resetEligibleUpsells');

  return dispatch('getCampaignEligibleUpsells', { campaignId });
};

export const addCampaignUpsell = async ({ rootState, state, dispatch, commit }, upsell) => {
  let response,
    payload = {
      discount: upsell.discount,
      default_side: upsell.default_side,
      u_cid: upsell.campaign_id,
      u_cpid: upsell.campaign_product_id,
    },
    upsellCount = state.currentCampaign.upsellData.upsells.length;

  response = await dispatch('post', { apiUrl: state.currentCampaign.upsellData._links.add_upsell.href, payload });
  response = await dispatch('get', state.currentCampaign.upsellData._links._self.href);
  response.data.upsells[upsellCount].isNew = true;
  commit('setCurrentCampaign', { property: 'upsellData', value: response.data, tab: campaignsEnum.UPSELLS });

  return response;
};

export const deleteCampaignUpsell = async ({ dispatch, commit, state }, upsell) => {
  let response;

  try {
    await swal({
      title: `Delete Upsell ${upsell.campaign_name}?`,
      type: 'question',
      showCancelButton: true,
      confirmButtonColor: config.ERROR_COLOR,
      confirmButtonText: 'Delete',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          response = await dispatch('del', upsell._links.delete.href);
          response = await dispatch('get', state.currentCampaign.upsellData._links._self.href);
          commit('setCurrentCampaign', { property: 'upsellData', value: response.data, tab: campaignsEnum.UPSELLS });

          return response;
        } catch (e) {
          return Promise.resolve();
        }
      },
    });
  } catch (e) {}

  return response;
};

export const updateCampaignUpsells = async ({ commit, dispatch, state }) => {
  try {
    let promises = [];

    each(state.currentCampaign.upsellData.upsells, upsell => {
      let payload = {
        discount: parseFloat(upsell.discount),
        default_side: upsell.default_side,
        upsell_campaign_product_id: upsell.default_product_id,
      };

      promises.push(dispatch('put', { apiUrl: upsell._links.update.href, payload }));
    });

    let response = await Promise.all(promises);
    response = await dispatch('get', state.currentCampaign.upsellData._links._self.href);
    commit('setCurrentCampaign', { property: 'upsellData', value: response.data, tab: campaignsEnum.UPSELLS });

    let upsells = state.currentCampaign.upsellData.upsells;
    if (upsells[0]) commit('updateState', { object: upsells[0], property: 'resetDirty', value: true });
    if (upsells[1]) commit('updateState', { object: upsells[1], property: 'resetDirty', value: true });

    return response
  } catch (e) {
    throw e;
  }
};

export const getRelaunchEstimate = async ({ commit, dispatch }, campaign) => {
  let response = await dispatch('get', campaign._links.get_relaunch_cost_estimate.href);

  commit('setRelaunchBasePrice', response.data.production_cost);

  return response
};

export const getProducts = async ({ dispatch, commit }, responseObj) => {
  let response = await Promise.all([dispatch('get', responseObj.campaign_products), dispatch('get', responseObj.eligible_products)]);

  commit('setCurrentCampaign', { property: campaignsEnum.PRODUCTS, value: response[0].data.products, tab: campaignsEnum.PRODUCTS });
  commit('setCurrentCampaignProducts', response[1].data);
  commit('resetEligibleUpsells');

  return response;
};

export const toggleProductActive = async ({ commit, dispatch, state }, product) => {
  let response,
    property, color;

  if (product.is_active) {
    property = 'deactivate';
    color = config.ERROR_COLOR;
  } else {
    property = 'activate';
    color = config.PRIMARY_COLOR;
  }

  try {
    await swal({
      title: `${property.capitalize()} ${product.style}?`,
      type: 'question',
      showCancelButton: true,
      confirmButtonColor: color,
      confirmButtonText: property.capitalize(),
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          response = await dispatch('put', { apiUrl: product._links[property].href });

          commit('setCurrentCampaign', { property: `cache.${campaignsEnum.UPSELLS}`, value: null });
          commit('setCurrentCampaign', { property: 'upsellData', value: {} });

          response = await dispatch('getProducts', response);

          if (product.id === state.currentCampaign.defaultProduct.id) {
            let newDefaultProduct = find(state.currentCampaign.firstProductsWithColors, product => product.is_active && !product.is_discontinued);

            commit('setCurrentCampaign', { property: campaignsEnum.DEFAULT_PRODUCT, value: newDefaultProduct });

            response = await dispatch('setDefaultProduct');

            commit('buildFirstProductsWithColors');
          }

          return response;
        } catch (e) {
          return Promise.resolve();
        }
      },
    });
  } catch (e) {}

  return response;
};

export const setDefaultProduct = async ({ commit, dispatch, state }) => {
  let payload = {
      color_id: state.currentCampaign.defaultProduct.color.id,
      side: state.currentCampaign.defaultProduct.default_side,
    },
    response = await dispatch('put', { apiUrl: state.currentCampaign.defaultProduct._links.make_default.href, payload });

  return response
};

export const updateProducts = async ({ commit, dispatch, state }) => {
  let promises = [],
    response,
    payload;

  each(state.currentCampaign.firstProductsWithColors, product => {
    payload = pick(product, 'selling_price', 'msrp');

    payload.colors = [];

    each(product.colors, color => {
      if (color.active) payload.colors.push(color.id);
    });

    if (product.dirty && !productValidator[product.type_id].hasErrors()) promises.push(dispatch('put', { apiUrl: product._links.update.href, payload }));
  });

  response = await Promise.all(promises);

  return response;
};

export const addProduct = async ({ dispatch, commit, state }, product) => {
  let payload = {
      product_type_id: product.id,
      colors: map(filter(product.colors, color => color.created), created => created.id),
      selling_price: 0, // product.selling_price,
    },
    response = await dispatch('post', { apiUrl: product._links.create_new.href, payload });

  commit('setCurrentCampaign', { property: `cache.${campaignsEnum.UPSELLS}`, value: null });
  commit('setCurrentCampaign', { property: 'upsellData', value: {} });

  await dispatch('getProducts', response);

  // used to display menu upon adding new product
  commit('updateState', { object: product, property: 'isNew', value: true });

  return response
};

export const updateCurrentCampaignPixels = async ({ dispatch, commit, state }) => {
  let response,
    payload = transformer.transformPixels(state.currentCampaign.pixels);

  response = await dispatch('put', { apiUrl: state.currentCampaign.pixels._links.update.href, payload });

  response = await dispatch('get', response.campaign_tracking);

  commit('setCurrentCampaign', { property: 'pixels', value: response.data.pixels, tab: campaignsEnum.TRACKING });
  commit('initializeCurrentCampaignFacebookCustomVars');

  return response;
};

export const updateSeoInfo = async ({ dispatch, commit, state }) => {
  let seo = state.currentCampaign.seo,

    response = await Promise.all([
      dispatch('put', { apiUrl: seo._links.update_meta.href, payload: transformer.transformMetadata(seo.viralstyle) }),
      dispatch('put', { apiUrl: seo._links.update_og.href, payload: transformer.transformOpengraph(seo.open_graph) }),
      dispatch('put', { apiUrl: seo._links.update_twitter.href, payload: transformer.transformTwitter(seo.twitter) }),
    ]);

  response = await dispatch('get', response[0].campaign_metadata);

  commit('setCurrentCampaign', { property: 'seo', value: response.data.metadata, tab: campaignsEnum.META });

  return response;
};

export const updateCampaignEmailList = async ({ dispatch, commit, state }) => {
  let response,
    payload = {
      email_list_id: state.currentCampaign.email.id
    };

  response = await dispatch('put', { apiUrl: state.currentCampaign.email._links.update.href, payload });

  response = await dispatch('get', response.campaign_email);

  commit('setCurrentCampaign', { property: campaignsEnum.EMAIL, value: response.data.list, tab: campaignsEnum.EMAIL });

  return response;
};

export const getEligibleProducts = async ({ state, dispatch, commit }, campaignId) => {
  let response;

  if (state.currentCampaign.cache.id !== campaignId) {
    commit('setCurrentCampaign', { property: 'cache', value: {} });
    commit('setCurrentCampaign', { property: 'cache.id', value: campaignId });
  }

  if (!state.currentCampaign.cache.addedEligibleProducts) {
    commit('updateState', { object: state.currentCampaign, property: 'firstProductsWithColors', value: [] });

    response = await dispatch('async', () => campaignsApi.getEligibleProducts(campaignId));

    commit('setCurrentCampaign', { property: 'cache.addedEligibleProducts', value: true });
    commit('setCurrentCampaignProducts', response.data);
  }

  return response;
};

export const variantLimitReached = ({ state, commit }) => {
  let text = `Cannot add more than ${state.maxProductVariants} product/color combinations.`;
  commit('addNotification', text);
};

export const getRelaunchHistory = async ({ dispatch, commit, state }, campaignId) => {
  let response;

  if (state.currentCampaign.cache.id !== campaignId) {
    commit('setCurrentCampaign', { property: 'cache', value: {} });
    commit('setCurrentCampaign', { property: 'cache.id', value: campaignId });
  }

  if (!state.currentCampaign.cache.relaunchHistory) {
    response = await dispatch('async', () => campaignsApi.getCampaignHistory(campaignId));

    commit('setCurrentCampaign', { property: 'cache.relaunchHistory', value: true });
    commit('updateState', { object: state, property: 'relaunchHistory', value: get(response.data, 'entries') });
  }

  return response;
};

export const checkIfCurrentRelaunchHasSales = ({ dispatch, commit }, campaignId) => new Promise(async (resolve, reject) => {
  let response = await dispatch('get', `${hateoas.url('search_client_campaign_orders', { clientId: storage.get('client-hash'), campaignId: campaignId })}?filters[]=campaign_ids:${campaignId}`);

  if (response.data.total_results) {
    resolve(true);
  } else {
    resolve(false);
  }
});

export const endCampaignEarlyFromRow = async ({ dispatch, commit }, campaign) => {
  let response,
    currentRelaunchHasSales = await dispatch('checkIfCurrentRelaunchHasSales', campaign.id);

  await swal({
    title: `End ${campaign.name} Early?`,
    type: currentRelaunchHasSales ? 'question' : 'warning',
    showCancelButton: true,
    confirmButtonColor: config.ERROR_COLOR,
    confirmButtonText: 'End',
    showLoaderOnConfirm: true,
    html: currentRelaunchHasSales ? null : '<h5 class="vs-text-error"><strong>This campaign\'s latest relaunch currently has zero sales. Ending early will permanently end the campaign.</strong></h5>',
    preConfirm: async () => {
      try {
        response = await dispatch('put', { apiUrl: campaign._links.end_early.href });

        commit('resetCampaigns', 'all');
        commit('resetStores');

        dispatch('getCampaigns', { type: 'all', lazy: false });
        dispatch('getCampaignsChartsData');

        return response;
      } catch (e) {
        commit('resetCampaigns', 'all');
        commit('resetStores');

        dispatch('getCampaigns', { type: 'all', lazy: false });
        dispatch('getCampaignsChartsData');

        return Promise.resolve();
      }
    },
  });

  return response;
};

export const seedEmailList = async ({ state, dispatch, commit }) => {
  let response = await dispatch('post', { apiUrl: state.currentCampaign.email._links.seed_mail_list.href, payload: { list_id: state.currentCampaign.email.id } });

  commit('addNotification', `Email list ${state.currentCampaign.email.name} seeded successfully.`);

  return response;
};

export const fetchChartsData = async ({ rootState, state, dispatch, commit }, { start, end, campaignId, latestRelaunchOnly }) => {
  start = start || moment().tz(rootState.currentUser.timezone).startOf('day');
  end = end || moment().tz(rootState.currentUser.timezone).endOf('day');

  _getVisitors(state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, 'visitors');
  _getReferrerAggregations(state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, 'utm');
  _getVisitorReferrals(state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, 'referrals');
  _getUpsellStats(state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, 'upsells');
  _getProductsAggregations(state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, 'products');
  _getVisitorsByDevice(state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, 'devices');
  _getGeoVisitors(state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, 'geoVisitors');
  _getHeatmap(state, dispatch, commit, start, end, campaignId, latestRelaunchOnly);
};

const _getVisitors = async (state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, loadingProperty) => {
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: true });

  let response = await dispatch('async', () => campaignsApi.getVisitors(start, end, campaignId, null, latestRelaunchOnly));

  commit('updateState', { object: state.aggregations, property: 'visitors', value: response.data });
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: false });

  return response;
};

const _getGeoVisitors = async (state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, loadingProperty) => {
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: true });

  let response = await dispatch('async', () => campaignsApi.getGeoVisitors(start, end, campaignId, latestRelaunchOnly)),

    regionalVisitorsWithConv = each(response.data, visitor => {
      let statesWithConv;

      visitor.conversion = Math.round(visitor.orders_count / visitor.visits * 100);

      if (visitor.country_iso === 'US' && get(visitor, 'provinces')) {
        statesWithConv = each(visitor.provinces, province => {
          province.conversion = Math.round(province.orders_count / province.visits * 100);
        });
      }

      if (statesWithConv) visitor.provinces = statesWithConv;
    });

  commit('updateState', { object: state.aggregations, property: 'regionalVisitors', value: regionalVisitorsWithConv });
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: false });

  return response;
};

const _getHeatmap = async (state, dispatch, commit, start, end, campaignId, latestRelaunchOnly) => {
  let response = await dispatch('async', () => campaignsApi.getHeatmap(start, end, campaignId, latestRelaunchOnly));
  commit('updateState', { object: state.aggregations, property: 'heatmap', value: response.data });
  return response;
};

const _getVisitorsByDevice = async (state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, loadingProperty) => {
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: true });

  let response = await dispatch('async', () => campaignsApi.getVisitorsByDevice(start, end, campaignId, latestRelaunchOnly)),

    visitorDevicesWithConv = each(response.data, device => {
      device.conversion = Math.round(device.orders_count / device.visits * 100);
    });

  commit('updateState', { object: state.aggregations, property: 'visitorDevices', value: visitorDevicesWithConv });
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: false });

  return response;
};

const _getVisitorReferrals = async (state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, loadingProperty) => {
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: true });

  let response = await dispatch('async', () => campaignsApi.getVisitorReferrals(start, end, campaignId, latestRelaunchOnly)),

    visitorReferralsWithConv = each(response.data, referral => {
      referral.conversion = Math.round(referral.orders / referral.visits * 100);
    });

  commit('updateState', { object: state.aggregations, property: 'visitorReferrals', value: visitorReferralsWithConv });
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: false });

  return response;
};

const _getReferrerAggregations = async (state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, loadingProperty) => {
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: true });

  let response = await dispatch('async', () => campaignsApi.getReferrerAggregations(start, end, campaignId, latestRelaunchOnly));

  commit('updateState', { object: state.aggregations, property: 'referrers', value: response.data });
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: false });

  return response;
};

const _getProductsAggregations = async (state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, loadingProperty) => {
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: true });

  let response = await dispatch('async', () => campaignsApi.getProductsAggregations(start, end, campaignId, latestRelaunchOnly));

  commit('updateState', { object: state.aggregations, property: 'products', value: response.data });
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: false });

  return response;
};

const _getUpsellStats = async (state, dispatch, commit, start, end, campaignId, latestRelaunchOnly, loadingProperty) => {
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: true });

  let response = await dispatch('async', () => campaignsApi.getUpsellStats(start, end, campaignId, latestRelaunchOnly)),
    upsellStats = response.data;

  each(upsellStats, upsell => upsell.conversion_rate = isNumber(upsell.visits) && isNumber(upsell.total_orders) && upsell.visits !== 0 ? upsell.total_orders / upsell.visits * 100 : 'N/A');

  commit('updateState', { object: state.aggregations, property: 'upsellStats', value: upsellStats });
  commit('updateState', { object: state.aggregations.loading, property: loadingProperty, value: false });

  return response;
};